import { useState, useEffect, useMemo, useRef } from "react";
import { Search, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const INDEX = [
  { title: "Home", desc: "Healing through story. Strength through connection — an Aunty beside children, young people and families.", path: "/", keywords: "home aunty yarn welcome start" },
  { title: "Our Story / About Ngawilyi", desc: "Why Ngawilyi exists, our philosophy, the Arabana name, and the Founder, CEO & Lead Narrative Therapist profile.", path: "/about", keywords: "about story founder ceo angel lead narrative therapist why organisation philosophy arabana" },
  { title: "Meet Our Aunties", desc: "Who a Ngawilyi Aunty is — narrative therapists with qualifications, safeguards, supervision and lived knowledge.", path: "/aunties", keywords: "aunties auntie team qualifications therapist narrative therapist supervision who" },
  { title: "Our Approach", desc: "How Ngawilyi works — story, connection, culture, movement, mentoring, community and knowledge.", path: "/approach", keywords: "approach model how narrative practice story connection culture movement mentoring community knowledge" },
  { title: "Programs — All", desc: "Every proposed Ngawilyi program, shaped with community.", path: "/programs", keywords: "programs program all services list" },
  { title: "Those Who Know, Not the Adults That Don't", desc: "Placement stability program recognising the knowledge of children who have lived care, placement change and family separation.", path: "/programs", keywords: "those who know placement stability foster kinship carer care ooHC out of home care voices" },
  { title: "Turning Story — Substance Use Support", desc: "Narrative mentoring for young people around alcohol and other drug use — never defined by the problem.", path: "/programs", keywords: "turning story substance use alcohol drugs aod harm reduction addiction" },
  { title: "Education Outside the Box", desc: "Education engagement through relationship — for children struggling within traditional schooling.", path: "/programs", keywords: "education school schools suspension disengagement teachers learning alternative" },
  { title: "Family Connection & Reunification", desc: "Support around family contact, reconnection, restoration and reunification — with children's hopes heard safely.", path: "/programs", keywords: "family restoration reunification reconnection contact parents carers visits" },
  { title: "Movement & Fitness Program", desc: "Walking, gym, outdoor activities and movement-based mentoring — easier to yarn side-by-side.", path: "/programs", keywords: "movement fitness gym walking hiking exercise outdoor" },
  { title: "Community Programs", desc: "Art & Cuppa with Aunties, Community Cook-Up, Yarning Circles and Community Movement.", path: "/community", keywords: "community art cuppa cook up yarning circles group food" },
  { title: "Threads — Finding the Threads", desc: "Reconnection, identity and the stories that sit between the records — narrative-informed exploration of Aboriginal family stories.", path: "/threads", keywords: "threads finding reconnection identity records country stolen generations family history" },
  { title: "Finding the Threads Guide & Reflection", desc: "A supported resource for exploring your own reconnection journey — available by request.", path: "/threads#guide-form", keywords: "guide reflection resource request threads workbook" },
  { title: "Finding the Threads Training", desc: "One-day professional learning workshop for organisations — identity, reconnection and disrupted family stories.", path: "/threads#training-form", keywords: "training workshop professional learning organisations workforce one day" },
  { title: "Private Supports", desc: "Private narrative therapy, mentoring and movement support — no referral or NDIS plan needed.", path: "/private-supports", keywords: "private therapy counselling individual couples family parent child sessions" },
  { title: "Private Support Fees", desc: "Narrative therapy 1-hour session fees and hourly rates for mentoring, movement and combined support.", path: "/private-supports#fees", keywords: "private fees price cost session rate therapy cost" },
  { title: "NDIS Supports", desc: "Mentoring, capacity building, skill development and community participation for people with an active NDIS plan.", path: "/ndis-supports", keywords: "ndis plan disability participant capacity building skills community participation funding" },
  { title: "Pricing & Fees", desc: "Suggested pricing across all Ngawilyi supports — sessions, mentoring, family work, consultation, training and packages.", path: "/pricing", keywords: "pricing fees price cost rates session cost therapy cost mentoring cost training cost ndis rates charge charges how much" },
  { title: "Request Support", desc: "What brings you to Ngawilyi? Private, NDIS, service-funded or just a yarn — find your pathway.", path: "/request-support", keywords: "request support access get help start pathway booking appointment session" },
  { title: "Make a Referral", desc: "Service-funded referrals from families, carers, schools, DCP, NGOs and community services.", path: "/referrals", keywords: "referral refer dcp child protection youth justice school ngo government professional" },
  { title: "Help Shape Ngawilyi — Community Consultation", desc: "Expression of interest for Elders, young people, carers, communities and professionals to co-design Ngawilyi.", path: "/consultation", keywords: "consultation community co-design shape expression interest eoi elders yarn roundtable" },
  { title: "Partner With Us", desc: "Nine ways to partner — pilots, ACCO collaboration, education, government, philanthropy, research and more.", path: "/partner", keywords: "partner partnership fund pilot philanthropic government research evaluation acco sponsorship collaborate" },
  { title: "Impact & Evaluation", desc: "How Ngawilyi intends to evaluate and share learning — honestly, with community.", path: "/impact", keywords: "impact evaluation evidence outcomes research data" },
  { title: "Culture", desc: "Cultural identity and connection — Arabana authorisation, and respect for every person's own Nation.", path: "/culture", keywords: "culture cultural identity arabana elders country nation aboriginal belonging" },
  { title: "Who We Support", desc: "Children, young people, parents, kinship carers, foster carers, communities, schools and professionals.", path: "/who-we-support", keywords: "who support children young people parents families kinship foster carers schools professionals" },
  { title: "Contact / Yarn With Us", desc: "Start with a yarn — email Ngawilyi.project@outlook.com or send a message.", path: "/contact", keywords: "contact email phone yarn talk message reach" },
];

const SUGGESTED = [
  "Pricing & Fees", "Foster & kinship carer support", "Family restoration", "Substance use", "Mentoring",
  "Narrative therapy", "Fitness", "NDIS", "Private therapy", "Schools & education",
  "Community consultation", "Training", "Referrals", "Partnering with us",
];

const lev = (a, b) => {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[m][n];
};

const search = (q) => {
  const tokens = q.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (!tokens.length) return [];
  return INDEX.map((e) => {
    const words = `${e.title} ${e.desc} ${e.keywords}`.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    let score = 0;
    for (const t of tokens) {
      let best = 0;
      for (const w of words) {
        if (w === t) best = Math.max(best, 3);
        else if (w.startsWith(t) || w.includes(t)) best = Math.max(best, 2);
        else if (t.length >= 4) {
          const d = lev(t, w);
          if (d <= (t.length >= 7 ? 2 : 1)) best = Math.max(best, 1);
        }
      }
      if (best === 0) return { e, score: 0 };
      score += best;
    }
    return { e, score };
  })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
};

const SearchOverlay = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const openSearch = () => setOpen(true);
    window.addEventListener("open-search", openSearch);
    return () => window.removeEventListener("open-search", openSearch);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => search(query), [query]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-charcoal/60 backdrop-blur-md px-4 py-6 sm:py-16 overflow-y-auto"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl mx-auto rounded-[40px] bg-cream grain relative overflow-hidden shadow-[0_40px_100px_rgba(45,42,38,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-blush/40 organic-blob blur-2xl pointer-events-none" />
            <div className="relative p-6 sm:p-10">
              <div className="flex items-center justify-between mb-6">
                <p className="font-display italic text-2xl text-charcoal">What are you looking for?</p>
                <button data-testid="search-close-btn" onClick={() => setOpen(false)} aria-label="Close search" className="rounded-full border border-earth/20 p-2.5 text-earth hover:text-terracotta hover:border-terracotta transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-terracotta" />
                <input
                  ref={inputRef}
                  data-testid="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try 'mentoring', 'NDIS', 'fees'…"
                  className="w-full rounded-full border border-earth/20 bg-creamdark pl-14 pr-6 py-4 text-lg text-charcoal placeholder:text-earth/40 outline-none transition-all duration-300 focus:border-terracotta focus:ring-4 focus:ring-terracotta/15"
                />
              </div>

              {!query && (
                <div className="mt-6">
                  <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-earth/60 mb-3">People often search for</p>
                  <div className="flex flex-wrap gap-2.5">
                    {SUGGESTED.map((s) => (
                      <button
                        key={s}
                        data-testid={`search-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => setQuery(s)}
                        className="rounded-full bg-creamdark border border-ochre/40 px-4 py-2 text-sm font-semibold text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {query && (
                <div className="mt-6" data-testid="search-results">
                  {results.length === 0 ? (
                    <div className="rounded-3xl bg-blush/30 border border-blush p-8 text-center">
                      <p className="font-display text-2xl text-charcoal">Nothing found for "{query}"</p>
                      <p className="mt-2 text-sm text-earth/75">Try different words — or just <a href="/contact" className="font-bold text-terracotta underline underline-offset-4">yarn with us</a> and we'll point you the right way.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {results.map(({ e }) => (
                        <a
                          key={e.path + e.title}
                          href={e.path}
                          data-testid={`search-result-${e.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="group flex items-start justify-between gap-4 rounded-3xl bg-creamdark border border-ochre/25 px-6 py-5 transition-all duration-300 hover:border-terracotta hover:-translate-y-0.5"
                        >
                          <div>
                            <p className="font-semibold text-charcoal group-hover:text-terracotta transition-colors">{e.title}</p>
                            <p className="mt-1 text-sm text-earth/75 leading-relaxed">{e.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 mt-1 shrink-0 text-terracotta transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
